import { getChatCompletion } from './chatCompletion';

export interface ExtractedLocalData {
  // Surfaces
  surfaceTotal?: number;
  surfaceRDC?: number;
  surfaceMezzanine?: number;
  surfaceSousSol?: number;
  surfaceEtage?: number;
  surfaceTerrasse?: number;
  // Façade
  facade?: number;
  lineaireVitrine?: number;
  nbVitrines?: number;
  // Commercial
  loyer?: number;
  droitEntree?: number;
  pasDePorte?: number;
  depotGarantie?: number;
  disponibilite?: string;
  // Technique
  hauteurPlafond?: number;
  puissanceElectrique?: string;
  extraction?: boolean;
  climatisation?: boolean;
  accessibilite?: boolean;
  parking?: number;
  livraison?: boolean;
  etatLocal?: string;
  // Localisation
  ville?: string;
  quartier?: string;
  adresse?: string;
  // Environnement
  fluxPieton?: string;
  visibiliteEnseigne?: string;
  voisinage?: string;
  proximiteTransports?: string;
  // Matching keywords (enrichment for matching engine)
  matchingKeywords?: string[];
  // Summary of what was extracted
  extractionSummary?: string;
}

const SYSTEM_PROMPT = `Tu es un expert en immobilier commercial au Maroc. 
Ton rôle est d'analyser des textes libres saisis par des brokers immobiliers et d'en extraire des données structurées.

Tu dois extraire UNIQUEMENT les informations explicitement mentionnées dans le texte.
Ne jamais inventer ou déduire des données non présentes.

Réponds UNIQUEMENT en JSON valide, sans markdown, sans explication.`;

const USER_PROMPT_TEMPLATE = (texts: { commentairesPublics: string; commentairesConfidentiels: string; travauxNecessaires: string }) => `
Analyse ces champs texte d'une fiche de local commercial et extrais les données structurées :

**Commentaires publics :**
${texts.commentairesPublics || '(vide)'}

**Commentaires confidentiels :**
${texts.commentairesConfidentiels || '(vide)'}

**Travaux nécessaires :**
${texts.travauxNecessaires || '(vide)'}

Extrais les données dans ce format JSON exact (inclure uniquement les champs trouvés, omettre les autres) :
{
  "surfaceTotal": <nombre en m² si mentionné>,
  "surfaceRDC": <nombre en m² si mentionné>,
  "surfaceMezzanine": <nombre en m² si mentionné>,
  "surfaceSousSol": <nombre en m² si mentionné>,
  "surfaceEtage": <nombre en m² si mentionné>,
  "surfaceTerrasse": <nombre en m² si mentionné>,
  "facade": <nombre en mètres si mentionné>,
  "lineaireVitrine": <nombre en mètres si mentionné>,
  "nbVitrines": <nombre entier si mentionné>,
  "loyer": <montant en MAD si mentionné>,
  "droitEntree": <montant en MAD si mentionné>,
  "pasDePorte": <montant en MAD si mentionné>,
  "depotGarantie": <montant en MAD si mentionné>,
  "disponibilite": <date ou "Immédiate" si mentionné>,
  "hauteurPlafond": <nombre en mètres si mentionné>,
  "puissanceElectrique": <valeur en kVA si mentionné>,
  "extraction": <true/false si mentionné>,
  "climatisation": <true/false si mentionné>,
  "accessibilite": <true/false si mentionné>,
  "parking": <nombre de places si mentionné>,
  "livraison": <true/false si mentionné>,
  "etatLocal": <"Neuf"|"Bon état"|"À rénover"|"Gros travaux" si mentionné>,
  "ville": <nom de ville si mentionné>,
  "quartier": <nom de quartier si mentionné>,
  "fluxPieton": <"Très fort"|"Fort"|"Moyen"|"Faible"|"Très faible" si mentionné>,
  "visibiliteEnseigne": <"Excellente"|"Bonne"|"Moyenne"|"Limitée" si mentionné>,
  "voisinage": <description du voisinage si mentionné>,
  "proximiteTransports": <description transports si mentionné>,
  "matchingKeywords": [<liste de mots-clés importants pour le matching : secteurs compatibles, contraintes, atouts>],
  "extractionSummary": "<résumé en 1-2 phrases de ce qui a été extrait>"
}`;

export async function analyzeLocalText(texts: {
  commentairesPublics: string;
  commentairesConfidentiels: string;
  travauxNecessaires: string;
}): Promise<ExtractedLocalData | null> {
  const hasContent =
    texts.commentairesPublics?.trim() ||
    texts.commentairesConfidentiels?.trim() ||
    texts.travauxNecessaires?.trim();

  if (!hasContent) return null;

  try {
    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4.1-mini',
      [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: USER_PROMPT_TEMPLATE(texts) },
      ],
      { max_completion_tokens: 1000 }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content) return null;

    // Clean potential markdown code blocks
    const cleaned = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const parsed = JSON.parse(cleaned);
    return parsed as ExtractedLocalData;
  } catch (err) {
    console.error('analyzeLocalText error:', err);
    return null;
  }
}
